/**
 * SOLID Review
 * S — Strong. One concern: the ad area's slot — its height, from the window's, and when its ad refreshes, by the game's phase.
 * O — Good. A new phase's interval is one more arm of `refreshIntervalOf`.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Strong. The slot's height and its last refresh; the view's two inputs.
 * D — Strong. The platform's viewport, page events, timers and clock.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions } from './Reactions';
import type { IConfigDepViewPlatform, TViewTimer } from './platform/IConfigDepViewPlatform';
import { GamePhase, MonetizationTrack } from '../../application/ports/ClientVocabulary';

const AD_REFRESH_ACTIVE_GAMEPLAY_MS = 60_000;
const AD_REFRESH_LOBBY_MS = 30_000;
/** The slot's height bounds — the view's box keeps them too. */
export const MAX_AD_HEIGHT_PX = 90;
export const MIN_AD_HEIGHT_PX = 50;
const AD_MAX_HEIGHT_PERCENT = 10;

export interface IStateAdContainer {
  readonly adHeight: number;
  /** When the slot's ad was last refreshed — a change asks the network to fill it again. */
  readonly lastRefreshedAt: number | null;
}

/** What the ad area's view hands it: the player's track and the game's phase. */
type TAdInputs = readonly [monetizationTrack: MonetizationTrack, currentPhase: GamePhase];

const FIRST: IStateAdContainer = { adHeight: MIN_AD_HEIGHT_PX, lastRefreshedAt: null };

/**
 * THE AD AREA'S SLOT (frontend-framework-oo P5, 2026-10-30, §28.297) — `components/layout/AdContainer.tsx`'s state and effects: the
 * slot is a tenth of the window's height, between 50 and 90 px, and follows its resizes; a free player's ad refreshes every 60 s in
 * active play and every 30 s otherwise, the chain re-armed when the phase or the track changes.
 */
export class AdContainerViewModel implements IServiceViewModel<IStateAdContainer> {
  private readonly state = new ObservableState<IStateAdContainer>(FIRST);
  private refreshTimer: TViewTimer | null = null;
  private readonly reactions = new Reactions<TAdInputs>([
    {
      deps: () => [],
      run: () => {
        const updateHeight = (): void => {
          const height: Partial<IStateAdContainer> = { adHeight: AdContainerViewModel.clampAdHeight(this.platform.viewport.height()) };
          this.state.update(height);
        };
        updateHeight();
        return this.platform.pageEvents.on('resize', updateHeight);
      },
    },
    {
      deps: (inputs) => inputs,
      run: ([monetizationTrack, currentPhase]) => {
        if (monetizationTrack !== MonetizationTrack.FREE) return undefined;
        this.scheduleRefresh(AdContainerViewModel.refreshIntervalOf(currentPhase));
        return () => {
          /* c8 ignore next -- the timer is always set by the time this stops; the false branch is unreachable */
          if (this.refreshTimer !== null) {
            this.platform.timers.clearTimeout(this.refreshTimer);
            this.refreshTimer = null;
          }
        };
      },
    },
  ]);

  constructor(private readonly platform: Pick<IConfigDepViewPlatform, 'viewport' | 'pageEvents' | 'timers' | 'clock'>) {}

  static refreshIntervalOf(phase: GamePhase): number {
    switch (phase) {
      case GamePhase.ROLLING:
      case GamePhase.PEEKING:
      case GamePhase.CLAIMING:
      case GamePhase.DECIDING:
      case GamePhase.REVEALING:
        return AD_REFRESH_ACTIVE_GAMEPLAY_MS;
      case GamePhase.LOBBY:
      case GamePhase.GAME_OVER:
      case GamePhase.ROUND_END:
        return AD_REFRESH_LOBBY_MS;
      /* c8 ignore next -- all GamePhase values are covered above; default is unreachable */
      default:
        return AD_REFRESH_LOBBY_MS;
    }
  }

  static clampAdHeight(viewportHeight: number): number {
    const percentHeight = Math.floor(viewportHeight * (AD_MAX_HEIGHT_PERCENT / 100));
    return Math.min(MAX_AD_HEIGHT_PX, Math.max(MIN_AD_HEIGHT_PX, percentHeight));
  }

  private scheduleRefresh(intervalMs: number): void {
    const { timers, clock } = this.platform;
    if (this.refreshTimer !== null) {
      timers.clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.refreshTimer = timers.setTimeout(() => {
      const refreshed: Partial<IStateAdContainer> = { lastRefreshedAt: clock.now() };
      this.state.update(refreshed);
      this.scheduleRefresh(intervalMs);
    }, intervalMs);
  }

  getSnapshot(): IStateAdContainer {
    return this.state.get();
  }

  subscribe(listener: () => void): () => void {
    return this.state.subscribe(listener);
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(inputs: readonly unknown[]): void {
    this.reactions.stopChanged(inputs as TAdInputs);
  }

  commit(inputs: readonly unknown[]): void {
    this.reactions.startChanged(inputs as TAdInputs);
  }
}
